import { Component, Input, OnInit } from '@angular/core';
import dayjs from 'dayjs';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import { formatDateNoYear, formatTimeRange } from 'src/app/util/format-utils';
import { Blueshift, JobApplicationAcceptanceStatus, JobIdAndAcceptanceStatus } from '@bluecrew/crew-member-backend-angular-client';

dayjs.extend(weekOfYear);

@Component({
  selector: 'app-blueshift-list',
  templateUrl: './blueshift-list.component.html',
  styleUrls: ['./blueshift-list.component.scss'],
})
export class BlueshiftListComponent implements OnInit {

  @Input() blueshifts: Blueshift[];
  @Input() directInvitesToBeRejected?: Blueshift[] = [];
  @Input() jobIdAndAcceptanceStatuses?: JobIdAndAcceptanceStatus[];
  /**
   * if true, this component will only display direct invites to be rejected, and none of the blueshifts input
   * if false, this component will only display blueshifts input
   * this is required because both blueshifts and directInvitesToBeRejected are used for week aggregation,
   * while only one of the two can be displayed using this component at any given time.
   *  */ 
  @Input() onlyShowDirectInvitesToBeRejected?: boolean = false;

  shouldShowAcceptanceStatuses = false;
  jobIdToAcceptanceStatus: Map<string, JobApplicationAcceptanceStatus>;
  blueshiftsSortedByWeek: Blueshift[][];

  constructor() { }

  ngOnInit() {
    this.blueshiftsSortedByWeek = this.getBlueshiftsSortedByWeek(this.blueshifts, this.directInvitesToBeRejected);
    if (this.jobIdAndAcceptanceStatuses) {
      this.shouldShowAcceptanceStatuses = true;
      this.jobIdToAcceptanceStatus = this.getJobIdToAcceptanceStatus(this.jobIdAndAcceptanceStatuses);
    } else {
      this.shouldShowAcceptanceStatuses = false;
    }
  }

  getAcceptanceStatusForBlueshift(blueshift: Blueshift): string {
    let acceptanceStatus: JobApplicationAcceptanceStatus = this.jobIdToAcceptanceStatus.get(blueshift.jobId);
    switch (acceptanceStatus) {
      case JobApplicationAcceptanceStatus.Success: {
        return 'ASSIGNED';
      }
      case JobApplicationAcceptanceStatus.AddedToWaitlist: {
        return 'WAITLISTED';
      }
      default: {
        return 'UNAVAILABLE';
      }
    }
  }

  getAcceptanceStatusStyling(blueshift: Blueshift): string {
    let acceptanceStatus: JobApplicationAcceptanceStatus = this.jobIdToAcceptanceStatus.get(blueshift.jobId);
    switch (acceptanceStatus) {
      case JobApplicationAcceptanceStatus.Success: {
        return 'assigned-blueshift';
      }
      case JobApplicationAcceptanceStatus.AddedToWaitlist: {
        return 'waitlisted-blueshift';
      }
      default: {
        return 'unavailable-blueshift';
      }
    }
  }

  getDateComponent(dateTime: string): string {
    return formatDateNoYear(dayjs(dateTime));
  }

  getShiftTimerange(blueshift: Blueshift): string {
    return formatTimeRange(dayjs(blueshift.startDateTime), dayjs(blueshift.endDateTime));
  }

  /**
   * Initializes an array that groups Blueshifts by their ISO Week (Monday - Sunday)
   * 
   * @param blueshifts all Blueshifts passed into this component
   * @param directInvitesToBeRejected direct invite Blueshifts that will be rejected for display to the CM 
   * @returns an array containing arrays of Blueshifts, aggregated by their ISO Week
   */
  private getBlueshiftsSortedByWeek(blueshifts: Blueshift[], directInvitesToBeRejected: Blueshift[]): Blueshift[][] {
    const sortedBlueshifts = this.getBlueshiftsSortedByStartTime(blueshifts.concat(directInvitesToBeRejected));
    const weeksInYear = dayjs(sortedBlueshifts[0].startDateTime).month(11).date(30).week(); // total number of weeks for current year
    const firstWeekNum = dayjs(sortedBlueshifts[0].startDateTime).week();
    let lastWeekNum = dayjs(sortedBlueshifts[sortedBlueshifts.length - 1].startDateTime).week();
    lastWeekNum = lastWeekNum < firstWeekNum ? weeksInYear + lastWeekNum : lastWeekNum; // account for last week in next year
    const blueshiftsToWeek = new Map();
    const blueshiftsByWeek = [];

    // set up mapping from week number to blueshifts
    for (let currWeekNum = firstWeekNum; currWeekNum <= lastWeekNum; currWeekNum++) {
      blueshiftsToWeek.set(currWeekNum, []);
    }

    // populate blueshift lists for each week
    sortedBlueshifts.forEach(blueshift => {
      if (this.shouldDisplayBlueshift(blueshift)) {
        const blueshiftWeekNum = dayjs(blueshift.startDateTime).week();
        const aggregateWeekNum = blueshiftWeekNum < firstWeekNum ? weeksInYear + blueshiftWeekNum : blueshiftWeekNum;
        const currVal = blueshiftsToWeek.get(aggregateWeekNum);
        currVal.push(blueshift);
        blueshiftsToWeek.set(aggregateWeekNum, currVal);
      }
    });

    // get blueshift weeks in order
    for (let currWeekNum = firstWeekNum; currWeekNum <= lastWeekNum; currWeekNum++) {
      blueshiftsByWeek.push(blueshiftsToWeek.get(currWeekNum));
    }
    return blueshiftsByWeek;
  }

  private getBlueshiftsSortedByStartTime(blueshifts: Blueshift[]): Blueshift[] {
    return [...blueshifts].sort((a, b) => (a.startDateTime < b.startDateTime ? -1 : 1));
  }

  private shouldDisplayBlueshift(blueshift: Blueshift): boolean {
    return this.onlyShowDirectInvitesToBeRejected ? this.directInvitesToBeRejected.includes(blueshift) : !this.directInvitesToBeRejected.includes(blueshift);
  }

  private getJobIdToAcceptanceStatus(jobIdAndAcceptanceStatuses: JobIdAndAcceptanceStatus[]): Map<string, JobApplicationAcceptanceStatus> {
    return new Map(jobIdAndAcceptanceStatuses.map(jobIdAndAcceptanceStatus => 
      [jobIdAndAcceptanceStatus.jobId, jobIdAndAcceptanceStatus.acceptanceStatus]));
  }
}
